import { fixedUi, fixedUiTpl } from '@/i18n/fixedUi';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import FieldHelpMark from '@/components/FieldHelpMark';
import RequiredMark from '@/tiktok-platform/components/RequiredMark';
import PublishImageUploadZone from '@/tiktok-platform/modules/products/components/PublishImageUploadZone';
import SaveSizeChartTemplateMetaModal from '@/tiktok-platform/modules/templates/SaveSizeChartTemplateMetaModal';
import { TIKTOK_SIZE_CHART_IMAGE_USE_CASE } from '@/tiktok-platform/utils/tiktokProductImage';
import {
  applyOfficialSizeTemplate,
  asSizeChartList,
  buildSizeChartRecords,
  buildTiktokSizeChartData,
  isOfficialSizeChartTemplatePayload,
  isTiktokSizeChartId,
  officialSizeChartDraftHasContent,
  officialSizeChartDraftSignature,
  officialSizeChartRecordsHaveValues,
  parseSizeChartClass,
  pickFilledSizeChartElements,
  pickSizeMetaLists,
  recordsFromTiktokSizeChartData,
  resolveOfficialSizeChartSeedRecords,
  resolveOfficialSizeTemplateIds,
  tiktokSizeChartId,
  validateOfficialSizeChartDraft,
} from '@/tiktok-platform/utils/tiktokOfficialSizeChart';
import {
  formatTiktokSizeChartMeasureLabel,
  getCachedTiktokSizeChartMeasureLabel,
  resolveTiktokSizeChartMeasureLabels,
} from '@/tiktok-platform/utils/tiktokSizeChartMeasurementLabels';
import { adminAPI } from '@/utils/adminApi';
import { tiktokPlatformAPI } from '@/utils/api';
import { showToast } from '@/utils/toast';
import { chartText } from '@/i18n/chartText';
import { useTranslation } from 'react-i18next';

const DEFAULT_CHART_NAME = '发品尺码表';

/**
 * Tiktok 官方尺码表面板（class.get / meta.get / settings.get / create）。
 * 默认建立尺码表；行数跟随主规格已选尺码；可从尺码库下拉带出测量值。
 *
 * @param {boolean} [officialMode] Admin 官方产品模板：从官方尺码库选用（不拉店铺平台库、不「保存为模板」）
 * @param {boolean} [hideLibraryActions] 尺码库新建/编辑场景：隐藏「从库选用 / 保存为模板」（本页即在建库）
 * @param {string} [emptySizeHint] 未选尺码时的提示文案
 */
const TiktokOfficialSizeChartPanel = forwardRef(function TiktokOfficialSizeChartPanel({
  shopId = '',
  /** Admin 官方模板：按 shopType 列官方尺码库；规则仍借 shopId（参考店） */
  officialMode = false,
  shopType = '',
  categoryId = '',
  productTypeId = '',
  categoryPathLabel = '',
  selectedSizeIds = [],
  /** 主规格已选尺码名称，决定表格行 */
  selectedSizeLabels = [],
  /** 保存弹窗预填款号 */
  defaultStyleNo = '',
  required = false,
  sizeClass = null,
  loading = false,
  errorMessage = '',
  /** 产品模板 / 草稿回填 */
  initialDraft = null,
  onDraftChange,
  /** 尺码表图片（size_chart.image，use_case=SIZE_CHART_IMAGE）：与平台模板二选一 */
  sizeChartImage = null,
  onSizeChartImageChange,
  /** 官方产品模板无店铺时跳过 TikTok 转图 */
  skipTiktokConvert = false,
  hideLibraryActions = false,
  emptySizeHint = fixedUi('请先在规格供应中选择尺码，尺寸表将按所选尺码自动增减行。'),
}, ref) {
  useTranslation('workbench');
  const [busy, setBusy] = useState(false);
  /** create：编辑建表（默认）；template：套装等多表仅选库提交；image：仅上传尺码表图片 */
  const [mode, setMode] = useState(sizeChartImage ? 'image' : 'create');
  const [metaRes, setMetaRes] = useState(null);
  const [settingsRes, setSettingsRes] = useState(null);
  /** 平台尺码表库（发品引用 businessId） */
  const [platformTemplates, setPlatformTemplates] = useState([]);
  /** 站内 IPE 尺码库（含款号 / 自动规则） */
  const [ipeTemplates, setIpeTemplates] = useState([]);
  const [selectedLibraryId, setSelectedLibraryId] = useState('');
  const [selectedBusinessIds, setSelectedBusinessIds] = useState([]);
  const [records, setRecords] = useState([]);
  const recordsRef = useRef(records);
  recordsRef.current = records;
  const [loadError, setLoadError] = useState('');
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  /** 测量列表头中文映射刷新标记（resolve 完成后递增） */
  const [measureLabelVersion, setMeasureLabelVersion] = useState(0);
  const selectedSizeLabelsRef = useRef(selectedSizeLabels);
  selectedSizeLabelsRef.current = selectedSizeLabels;
  const initialDraftRef = useRef(initialDraft);
  initialDraftRef.current = initialDraft;
  const sizeChartImageRef = useRef(sizeChartImage);
  sizeChartImageRef.current = sizeChartImage;
  const draftSeededKeyRef = useRef('');
  const onDraftChangeRef = useRef(onDraftChange);
  onDraftChangeRef.current = onDraftChange;
  const lastDraftSigRef = useRef('');

  const { groupList, elementList } = useMemo(
    () => pickSizeMetaLists(metaRes, settingsRes),
    [metaRes, settingsRes],
  );
  const groupId = String(groupList[0]?.id ?? 1);

  useEffect(() => {
    if (!elementList.length) return undefined;
    let cancelled = false;
    void resolveTiktokSizeChartMeasureLabels(elementList)
      .then(() => {
        if (!cancelled) setMeasureLabelVersion((v) => v + 1);
      })
      .catch(() => {
        // 翻译失败时仍展示英文表头
      });
    return () => { cancelled = true; };
  }, [elementList]);
  const needCount = Number(sizeClass?.classType) === 1 ? 2 : 1;
  const suitMulti = needCount > 1;

  const normalizedSizeLabels = useMemo(
    () => (selectedSizeLabels || []).map((n) => String(n || '').trim()).filter(Boolean),
    [selectedSizeLabels],
  );

  /** 尺码库下拉：站内 / 官方库；店铺模式可再并入平台库 */
  const librarySelectOptions = useMemo(() => {
    const rows = [];
    const seenLabels = new Set();
    const seenPlatformIds = new Set();
    (ipeTemplates || []).forEach((t) => {
      const id = String(t.id || '').trim();
      if (!id) return;
      const styleNo = String(t.styleNo || '').trim();
      const label = styleNo
        ? fixedUiTpl('{0} · {1}', styleNo, t.name || fixedUiTpl('尺码表#{0}', id))
        : (t.name || fixedUiTpl('尺码表#{0}', id));
      seenLabels.add(label.toLowerCase());
      const platformId = tiktokSizeChartId(t.platformBusinessId || t.businessId);
      if (platformId) seenPlatformIds.add(platformId);
      rows.push({ value: `ipe:${id}`, label });
    });
    // 官方产品模板只列官方尺码库，不并入参考店平台库
    if (!officialMode) {
      (platformTemplates || []).forEach((t) => {
        const id = tiktokSizeChartId(t.businessId || t.platformBusinessId || t.id);
        if (!id) return;
        if (seenPlatformIds.has(id)) return;
        const label = String(t.name || fixedUiTpl('尺码表#{0}', id));
        if (seenLabels.has(label.toLowerCase())) return;
        seenLabels.add(label.toLowerCase());
        seenPlatformIds.add(id);
        rows.push({ value: id, label });
      });
    }
    return rows;
  }, [ipeTemplates, platformTemplates, officialMode]);

  const syncRowsToSizeLabels = useCallback((labels, groups, elements, prevRecords = []) => {
    return buildSizeChartRecords({
      groupList: groups,
      elementList: elements,
      sizeNames: labels,
      prevRecords,
    });
  }, []);

  const reloadIpeTemplates = useCallback(async () => {
    if (hideLibraryActions) {
      setIpeTemplates([]);
      return;
    }
    const typeKey = String(productTypeId || categoryId || '').trim();
    if (!typeKey || (officialMode ? !shopType : !shopId)) {
      setIpeTemplates([]);
      return;
    }
    try {
      const res = officialMode
        ? await adminAPI.listOfficialTiktokSizeChartTemplates({
          shopType,
          productTypeId: typeKey,
          page: 1,
          pageSize: 100,
        })
        : await tiktokPlatformAPI.listSizeChartTemplates(shopId, {
          productTypeId: typeKey,
          page: 1,
          pageSize: 100,
        });
      setIpeTemplates(Array.isArray(res?.items) ? res.items : []);
    } catch {
      setIpeTemplates([]);
    }
  }, [hideLibraryActions, officialMode, shopId, shopType, productTypeId, categoryId]);

  const loadContext = useCallback(async () => {
    if (!shopId || !categoryId || !required || !sizeClass?.classId) {
      setMode('skip');
      setMetaRes(null);
      setSettingsRes(null);
      setPlatformTemplates([]);
      setSelectedLibraryId('');
      setSelectedBusinessIds([]);
      setRecords([]);
      draftSeededKeyRef.current = '';
      return;
    }
    setBusy(true);
    setLoadError('');
    try {
      const classId = Number(sizeClass.classId);
      const catId = Number(categoryId);
      const seedKey = `${officialMode ? `official:${shopType}` : shopId}|${catId}|${classId}`;
      const skipPlatformList = hideLibraryActions || officialMode;
      const [meta, settings, templatesRes] = await Promise.all([
        tiktokPlatformAPI.getPublishSizeChartMeta(shopId, catId, classId),
        tiktokPlatformAPI.getPublishSizeChartSettings(shopId, catId, classId),
        skipPlatformList
          ? Promise.resolve(null)
          : tiktokPlatformAPI.listPublishSizeChartTemplates(shopId, {
            catId,
            offset: 0,
            pageSize: 50,
          }),
      ]);
      setMetaRes(meta);
      setSettingsRes(settings);
      setPlatformTemplates(skipPlatformList ? [] : asSizeChartList(templatesRes));
      if (!hideLibraryActions) {
        void reloadIpeTemplates();
      }
      const { groupList: groups, elementList: elements } = pickSizeMetaLists(meta, settings);
      const labels = (selectedSizeLabelsRef.current || [])
        .map((n) => String(n || '').trim())
        .filter(Boolean);
      const draft = initialDraftRef.current;
      const draftHasContent = officialSizeChartDraftHasContent(draft, groups);
      const canSeed = draftHasContent && draftSeededKeyRef.current !== seedKey;
      if (canSeed) {
        draftSeededKeyRef.current = seedKey;
        if (hideLibraryActions) {
          setMode('create');
        } else if (draft.mode === 'template' || draft.mode === 'create' || draft.mode === 'skip' || draft.mode === 'image') {
          setMode(draft.mode === 'image' && sizeChartImageRef.current ? 'image' : (draft.mode === 'image' ? 'create' : draft.mode));
        } else {
          setMode(Number(sizeClass?.classType) === 1 ? 'template' : 'create');
        }
        const seededRecords = resolveOfficialSizeChartSeedRecords(draft, groups, elements);
        setRecords(syncRowsToSizeLabels(labels, groups, elements, seededRecords));
        setSelectedBusinessIds(
          hideLibraryActions
            ? []
            : (Array.isArray(draft.selectedBusinessIds) ? draft.selectedBusinessIds : [])
              .map((id) => tiktokSizeChartId(id))
              .filter(Boolean),
        );
        setSelectedLibraryId(hideLibraryActions ? '' : String(draft.selectedLibraryId || '').trim());
      } else if (draftSeededKeyRef.current !== seedKey) {
        setRecords(syncRowsToSizeLabels(labels, groups, elements, []));
        // 无草稿内容：若已上传尺码表图片，保持 image 模式（不强制建表，TK-PUB-27）
        setMode(hideLibraryActions
          ? 'create'
          : (sizeChartImageRef.current
            ? 'image'
            : (Number(sizeClass?.classType) === 1 ? 'template' : 'create')));
        setSelectedLibraryId('');
        setSelectedBusinessIds([]);
        if (draftHasContent) {
          draftSeededKeyRef.current = seedKey;
        }
      }
    } catch (e) {
      setLoadError(e?.message || chartText('sizeChartPanel.rulesFail', '尺码表规则加载失败'));
      setMode('create');
    } finally {
      setBusy(false);
    }
  }, [
    shopId,
    officialMode,
    shopType,
    categoryId,
    required,
    sizeClass?.classId,
    sizeClass?.classType,
    syncRowsToSizeLabels,
    hideLibraryActions,
    reloadIpeTemplates,
  ]);

  useEffect(() => {
    loadContext();
  }, [loadContext]);

  useEffect(() => {
    initialDraftRef.current = initialDraft;
    if (busy || loading || !required || !sizeClass?.classId) return;
    const draft = initialDraft;
    if (!officialSizeChartDraftHasContent(draft, groupList)) return;
    if (officialSizeChartRecordsHaveValues(recordsRef.current, groupList)) return;
    const seedKey = `${shopId}|${Number(categoryId)}|${Number(sizeClass.classId)}`;
    draftSeededKeyRef.current = seedKey;
    if (draft.mode === 'template' || draft.mode === 'create') {
      setMode(draft.mode);
    }
    setRecords(syncRowsToSizeLabels(
      normalizedSizeLabels,
      groupList,
      elementList,
      resolveOfficialSizeChartSeedRecords(draft, groupList, elementList),
    ));
    if (Array.isArray(draft.selectedBusinessIds)) {
      setSelectedBusinessIds(
        draft.selectedBusinessIds.map((id) => tiktokSizeChartId(id)).filter(Boolean),
      );
    }
    if (draft.selectedLibraryId != null) {
      setSelectedLibraryId(String(draft.selectedLibraryId || '').trim());
    }
  }, [
    initialDraft,
    busy,
    loading,
    required,
    sizeClass?.classId,
    shopId,
    categoryId,
    groupList,
    elementList,
    normalizedSizeLabels,
    syncRowsToSizeLabels,
  ]);

  useEffect(() => {
    if (busy || loading || mode === 'skip') return;
    setRecords((prev) => {
      const source = officialSizeChartRecordsHaveValues(prev, groupList)
        ? prev
        : (officialSizeChartDraftHasContent(initialDraftRef.current, groupList)
          ? resolveOfficialSizeChartSeedRecords(initialDraftRef.current, groupList, elementList)
          : prev);
      return syncRowsToSizeLabels(normalizedSizeLabels, groupList, elementList, source);
    });
  }, [normalizedSizeLabels, groupList, elementList, busy, loading, mode, syncRowsToSizeLabels]);

  useEffect(() => {
    if (typeof onDraftChangeRef.current !== 'function') return;
    if (busy || loading || mode === 'skip') return;
    // meta 未就绪时 sync 可能暂时丢掉测量列：禁止把空表回写父级，否则产品模板保存/再编辑会空
    const draftHasValues = officialSizeChartRecordsHaveValues(
      initialDraftRef.current?.records,
      groupList,
    );
    const liveHasValues = officialSizeChartRecordsHaveValues(records, groupList);
    if (draftHasValues && !liveHasValues && !(elementList || []).length) {
      return;
    }
    const payload = {
      mode,
      sizeClass,
      selectedBusinessIds,
      selectedLibraryId,
      records,
      groupList,
      elementList,
      ...(initialDraftRef.current?.sizeChartData && !liveHasValues
        ? { sizeChartData: initialDraftRef.current.sizeChartData }
        : {}),
    };
    if (liveHasValues && mode === 'create') {
      const picked = pickFilledSizeChartElements({ records, groupList, elementList });
      if (picked.ok && picked.elementList.length) {
        payload.sizeChartData = buildTiktokSizeChartData({
          groupList,
          elementList: picked.elementList,
          records,
          measurementType: metaRes?.measurementType || '',
        });
      }
    }
    const sig = officialSizeChartDraftSignature(payload);
    // 内容未变则不回调：父组件常传内联 onDraftChange，否则会 Maximum update depth
    if (sig && sig === lastDraftSigRef.current) return;
    lastDraftSigRef.current = sig;
    onDraftChangeRef.current(payload);
  }, [
    busy,
    loading,
    mode,
    sizeClass,
    selectedBusinessIds,
    selectedLibraryId,
    records,
    groupList,
    elementList,
    metaRes,
  ]);

  useImperativeHandle(ref, () => ({
    /** 保存产品模板前同步取面板最新草稿（避免 onDraftChange 尚未刷进 form） */
    getDraft() {
      if (mode === 'skip') return null;
      const payload = {
        mode,
        sizeClass,
        selectedBusinessIds: [...selectedBusinessIds],
        selectedLibraryId,
        records: Array.isArray(records) ? records.map((row) => ({
          values: row?.values && typeof row.values === 'object' ? { ...row.values } : {},
        })) : [],
        groupList,
        elementList,
      };
      if (mode === 'create' && payload.records.length) {
        const picked = pickFilledSizeChartElements({
          records: payload.records,
          groupList,
          elementList,
        });
        if (picked.ok && picked.elementList.length) {
          payload.sizeChartData = buildTiktokSizeChartData({
            groupList,
            elementList: picked.elementList,
            records: payload.records,
            measurementType: metaRes?.measurementType || '',
          });
        }
      }
      return payload;
    },
    validate() {
      if (required && mode === 'image') {
        if (!sizeChartImage) {
          return { ok: false, message: chartText('sizeChartPanel.needImageOrChart', '请上传尺码表图片，或切换到「尺码表」填写尺寸') };
        }
        return { ok: true };
      }
      return validateOfficialSizeChartDraft({
        required,
        mode,
        sizeClass,
        selectedBusinessIds,
        records,
        groupList,
        elementList,
      });
    },
    async resolveForSubmit() {
      if (required && mode === 'image') {
        if (!sizeChartImage) {
          throw new Error(chartText('sizeChartPanel.needImageOrChart', '请上传尺码表图片，或切换到「尺码表」填写尺寸'));
        }
        return [];
      }
      const check = validateOfficialSizeChartDraft({
        required,
        mode,
        sizeClass,
        selectedBusinessIds,
        records,
        groupList,
        elementList,
      });
      if (!check.ok) throw new Error(check.message);
      return resolveOfficialSizeTemplateIds({
        shopId,
        catId: categoryId,
        required,
        mode,
        sizeClass,
        selectedBusinessIds,
        chartName: DEFAULT_CHART_NAME,
        records,
        groupList,
        elementList,
        measurementType: metaRes?.measurementType || '',
        api: tiktokPlatformAPI,
      });
    },
  }), [
    required, mode, sizeClass, selectedBusinessIds, selectedLibraryId, records,
    groupList, elementList, shopId, categoryId, metaRes, sizeChartImage,
  ]);

  const resolvePlatformTemplateRow = async (row) => {
    let applied = applyOfficialSizeTemplate(row, { groupList, elementList });
    if (applied.records?.length) return applied;
    // 列表项常无 payload：用站内 id 拉详情再解析
    const localId = String(row?.id || row?.templateId || '').trim();
    if (!shopId || !localId || localId === tiktokSizeChartId(row?.businessId || row?.platformBusinessId)) {
      return applied;
    }
    try {
      const res = await tiktokPlatformAPI.getSizeChartTemplate(shopId, localId);
      const item = res?.item || res || {};
      const payload = item.payload && typeof item.payload === 'object' ? item.payload : {};
      return applyOfficialSizeTemplate(
        {
          ...item,
          ...payload,
          businessId: tiktokSizeChartId(
            row.businessId || row.platformBusinessId || item.platformBusinessId || item.businessId,
          ),
        },
        { groupList, elementList },
      );
    } catch {
      return applied;
    }
  };

  const applyPlatformTemplate = async (row) => {
    const businessId = tiktokSizeChartId(
      row.businessId || row.platformBusinessId || row.size_chart_id || row.id,
    );
    if (suitMulti) {
      if (!businessId) {
        showToast(chartText('sizeChartPanel.missingPlatformId', '该尺码表缺少平台 ID'), 'warning');
        return;
      }
      setSelectedBusinessIds((prev) => {
        const next = prev.map(String);
        if (next.includes(businessId)) return prev;
        if (next.length >= needCount) {
          return [...next.slice(1), businessId];
        }
        return [...next, businessId];
      });
      setMode('template');
      showToast(chartText('sizeChartPanel.pickedChart', `已选择尺码表「${row.name || businessId}」`, { name: row.name || businessId }), 'success');
      return;
    }

    if (!normalizedSizeLabels.length) {
      showToast(chartText('sizeChartPanel.pickSizeBeforeLib', '请先在规格供应中选择尺码，再从尺码表库带出尺寸'), 'warning');
      setSelectedLibraryId('');
      return;
    }

    const applied = await resolvePlatformTemplateRow(row);
    if (applied.records?.length) {
      setRecords(syncRowsToSizeLabels(
        normalizedSizeLabels,
        groupList,
        elementList,
        applied.records,
      ));
      setSelectedBusinessIds([]);
      setMode('create');
      showToast(
        applied.filled
          ? chartText('sizeChartPanel.filledFromLib', `已从尺码表库带出尺寸（${applied.filled} 项），可继续修改`, { count: applied.filled })
          : chartText('sizeChartPanel.pickedCheck', '已选用尺码表库，请核对尺寸'),
        'success',
      );
      return;
    }
    if (isTiktokSizeChartId(businessId)) {
      setSelectedBusinessIds([businessId]);
      setMode('template');
      showToast(chartText('sizeChartPanel.pickedReuse', `已选用「${row.name || businessId}」（将按已有模板发品）`, { name: row.name || businessId }), 'success');
      return;
    }
    showToast(chartText('sizeChartPanel.noSizeData', '该尺码表无可用尺寸数据'), 'warning');
  };

  const applyIpeTemplate = async (templateId) => {
    if (!templateId) return;
    if (officialMode ? !shopType : !shopId) return;
    if (!normalizedSizeLabels.length) {
      showToast(chartText('sizeChartPanel.pickSizeBeforeIpe', '请先在规格供应中选择尺码，再从尺码库带出尺寸'), 'warning');
      setSelectedLibraryId('');
      return;
    }
    try {
      const res = officialMode
        ? await adminAPI.getOfficialTiktokSizeChartTemplate(templateId)
        : await tiktokPlatformAPI.getSizeChartTemplate(shopId, templateId);
      const item = res?.item || res || {};
      const payload = item.payload && typeof item.payload === 'object' ? item.payload : {};
      if (!isOfficialSizeChartTemplatePayload(payload)) {
        showToast(
          officialMode
            ? chartText('sizeChartPanel.officialBadFormat', '该官方尺码库不是可套用格式，请在官方尺码库重新保存后再用')
            : chartText('sizeChartPanel.badFormat', '该尺码库模板不是官方尺码表格式，请重新保存后再用'),
          'warning',
        );
        return;
      }
      const draft = payload.officialSizeChart && typeof payload.officialSizeChart === 'object'
        ? payload.officialSizeChart
        : null;
      let seeded = Array.isArray(draft?.records) && draft.records.length
        ? draft.records
        : [];
      if (!seeded.length) {
        const sizeChartData = payload.sizeChartData
          || payload.size_chart_data
          || draft?.sizeChartData
          || (Array.isArray(payload.sizeChartDatas)
            ? (payload.sizeChartDatas[0]?.size_chart_data || payload.sizeChartDatas[0]?.sizeChartData)
            : null);
        const converted = recordsFromTiktokSizeChartData(sizeChartData, { groupList, elementList });
        seeded = converted?.records || [];
      }
      if (!seeded.length) {
        const applied = applyOfficialSizeTemplate(
          { ...item, ...payload, businessId: templateId },
          { groupList, elementList },
        );
        seeded = applied?.records || [];
      }
      if (!seeded.length) {
        showToast(chartText('sizeChartPanel.noTemplateData', '该尺码库模板没有尺寸数据'), 'warning');
        return;
      }
      setRecords(syncRowsToSizeLabels(normalizedSizeLabels, groupList, elementList, seeded));
      const platformIds = (Array.isArray(payload.platformBusinessIds) ? payload.platformBusinessIds : [])
        .map((id) => tiktokSizeChartId(id))
        .filter(Boolean);
      const fallbackPlatformId = tiktokSizeChartId(item.platformBusinessId || item.businessId);
      const ids = platformIds.length ? platformIds : (fallbackPlatformId ? [fallbackPlatformId] : []);
      setSelectedBusinessIds(ids);
      // 官方产品模板：只把测量值带进「创建尺码表」，不引用店铺平台 template id 发品
      setMode(officialMode ? 'create' : (ids.length && !seeded.length ? 'template' : 'create'));
      if (officialMode) setSelectedBusinessIds([]);
      showToast(
        officialMode
          ? chartText('sizeChartPanel.fromOfficial', `已从官方尺码库带出「${item.name || templateId}」`, { name: item.name || templateId })
          : chartText('sizeChartPanel.fromLib', `已从尺码库带出「${item.name || templateId}」`, { name: item.name || templateId }),
        'success',
      );
    } catch (e) {
      showToast(e?.message || chartText('sizeChartPanel.loadTemplateFail', '加载尺码库模板失败'), 'error');
      setSelectedLibraryId('');
    }
  };

  const applyLibraryTemplate = (rawValue) => {
    const value = String(rawValue || '').trim();
    setSelectedLibraryId(value);
    if (!value) {
      // 仅解除尺码库/平台模板引用；已带入或手改的测量值保留，不随下拉清空
      setSelectedBusinessIds([]);
      if (suitMulti) {
        setMode('template');
      } else if (mode === 'template') {
        setMode('create');
      }
      return;
    }
    if (value.startsWith('ipe:')) {
      void applyIpeTemplate(value.slice(4));
      return;
    }
    const want = tiktokSizeChartId(value);
    const row = platformTemplates.find((t) => (
      tiktokSizeChartId(t.businessId) === want
      || tiktokSizeChartId(t.platformBusinessId) === want
      || tiktokSizeChartId(t.id) === want
    ));
    if (row) {
      void applyPlatformTemplate(row);
      return;
    }
    // 下拉值是平台 ID、但站内列表按款号/类目过滤未带出时：用 IPE 库按 platformBusinessId 兜底
    const ipeHit = (ipeTemplates || []).find((t) => (
      tiktokSizeChartId(t.platformBusinessId) === want
      || tiktokSizeChartId(t.businessId) === want
      || String(t.id || '').trim() === want
    ));
    if (ipeHit?.id) {
      void applyIpeTemplate(String(ipeHit.id));
      return;
    }
    showToast(chartText('sizeChartPanel.chartMissing', '未找到所选尺码表'), 'error');
  };

  /**
   * 尺码表图片（TK-PUB-27）：官方允许「尺码表 / 尺码表图片」二选一。
   * 选图片维度后在此上传；清空图片时仍留在图片维度。
   */
  const applySizeChartImage = (img) => {
    onSizeChartImageChange?.(img || null);
  };

  const switchSizeChartDimension = (next) => {
    if (next === 'image') {
      if (mode === 'image') return;
      setMode('image');
      return;
    }
    // 切回尺码表：清掉图片，避免与表格同时提交
    if (mode === 'image') {
      onSizeChartImageChange?.(null);
    }
    setMode(suitMulti ? 'template' : 'create');
  };

  const openSaveTemplateModal = () => {
    if (suitMulti) {
      showToast(chartText('sizeChartPanel.suitUseLib', '套装类目请从尺码表库选用已有模板'), 'warning');
      return;
    }
    if (!normalizedSizeLabels.length) {
      showToast(chartText('sizeChartPanel.fillSizesFirst', '请先在规格供应中选择尺码并填写尺寸'), 'warning');
      return;
    }
    if (!productTypeId) {
      showToast(chartText('sizeChartPanel.missingProductType', '缺少商品类型，请重新选择类目后再保存'), 'warning');
      return;
    }
    const check = validateOfficialSizeChartDraft({
      required: true,
      mode: 'create',
      sizeClass,
      records,
      groupList,
      elementList,
    });
    if (!check.ok) {
      showToast(check.message || chartText('sizeChartPanel.fillComplete', '请先完整填写尺寸信息'), 'error');
      return;
    }
    setSaveModalOpen(true);
  };

  const officialDraftForSave = useMemo(() => ({
    mode: 'create',
    sizeClass,
    selectedBusinessIds: [],
    selectedLibraryId: '',
    records,
    groupList,
    elementList,
    /** 平台 measurement_type，保存/同步平台尺码表时回传 */
    measurementType: metaRes?.measurementType || '',
  }), [sizeClass, records, groupList, elementList, metaRes]);

  if (!categoryId) {
    return (
      <div className="tko-size-chart tko-official-size-chart" data-field-error="sizeChart">
        <div className="tko-size-chart__head">
          <div className="tko-size-chart__title-row">
            <h4 className="tko-publish__subhead">{chartText('sizeChartPanel.sectionTitle', fixedUi("尺寸信息"))}</h4>
          </div>
        </div>
        <p className="tko-wizard__inline-hint">{chartText('sizeChartPanel.pickCategory', fixedUi("请先选择类目"))}</p>
      </div>
    );
  }

  if (!shopId) {
    return (
      <div className="tko-size-chart tko-official-size-chart" data-field-error="sizeChart">
        <div className="tko-size-chart__head">
          <div className="tko-size-chart__title-row">
            <h4 className="tko-publish__subhead">{chartText('sizeChartPanel.sectionTitle', fixedUi("尺寸信息"))}</h4>
          </div>
        </div>
        <p className="tko-wizard__inline-hint">{chartText('sizeChartPanel.needRefShop', fixedUi("请先选择参考店铺以加载尺码表填写规则"))}</p>
      </div>
    );
  }

  if (!required) {
    return (
      <div className="tko-size-chart tko-official-size-chart" data-field-error="sizeChart">
        <div className="tko-size-chart__head">
          <div className="tko-size-chart__title-row">
            <h4 className="tko-publish__subhead">{chartText('sizeChartPanel.sectionTitle', fixedUi("尺寸信息"))}</h4>
          </div>
        </div>
        <p className="tko-wizard__inline-hint">{chartText('sizeChartPanel.notNeeded', fixedUi("该类目无需填写尺码表。"))}</p>
      </div>
    );
  }

  return (
    <div className="tko-size-chart tko-official-size-chart" data-field-error="sizeChart">
      <div className="tko-size-chart__head">
        <div className="tko-size-chart__title-row">
          <h4 className="tko-publish__subhead">
            {chartText('sizeChartPanel.sectionTitle', '尺寸信息')} <RequiredMark />
          </h4>
        </div>
        {errorMessage ? <div className="tko-field__error" role="alert">{errorMessage}</div> : null}
        {loadError ? <div className="tko-field__error" role="alert">{loadError}</div> : null}
      </div>

      {(busy || loading) ? (
        <div className="tko-attr-loading tko-size-chart-loading" role="status" aria-live="polite" aria-busy="true">
          <div className="tko-attr-loading__banner">
            <span className="tko-attr-loading__spinner" aria-hidden />
            <div className="tko-attr-loading__copy">
              <strong>{chartText('sizeChartPanel.loadingTitle', fixedUi("正在加载尺码表"))}</strong>
              <span>{chartText('sizeChartPanel.loadingHint', fixedUi("正在拉取测量项与尺码库，请稍候…"))}</span>
            </div>
          </div>
          <div className="tko-size-chart-loading__skeleton" aria-hidden>
            <div className="tko-attr-loading__line tko-attr-loading__line--field" />
            <div className="tko-size-chart-loading__table">
              {[0, 1, 2, 3].map((row) => (
                <div key={row} className="tko-size-chart-loading__row">
                  <i className="tko-attr-loading__line" />
                  <i className="tko-attr-loading__line" />
                  <i className="tko-attr-loading__line" />
                  <i className="tko-attr-loading__line" />
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {!(busy || loading) ? (
        <>
          <div
            className="tko-official-size-chart__modes"
            role="radiogroup"
            aria-label={chartText('sizeChartPanel.fillMode', '尺寸信息填写方式')}
          >
            <label className={`tko-official-size-chart__mode${mode !== 'image' ? ' is-on' : ''}`}>
              <input
                type="radio"
                name="tko-size-chart-dimension"
                checked={mode !== 'image'}
                onChange={() => switchSizeChartDimension('chart')}
              />
              <span>{chartText('sizeChartPanel.createChart', fixedUi("创建尺码表"))}</span>
            </label>
            <label className={`tko-official-size-chart__mode${mode === 'image' ? ' is-on' : ''}`}>
              <input
                type="radio"
                name="tko-size-chart-dimension"
                checked={mode === 'image'}
                onChange={() => switchSizeChartDimension('image')}
              />
              <span>{chartText('sizeChartPanel.uploadImage', fixedUi("上传图片"))}</span>
              <FieldHelpMark text={chartText('sizeChartPanel.imageHelp', '请上传清晰的尺码表图片，要求使用本地语言、图片主体为尺码表，分辨率不低于 1000 x 1000。')} />
            </label>
          </div>

          {mode === 'image' ? (
            (shopId || skipTiktokConvert) ? (
              <div className="tko-size-chart__image is-active">
                <PublishImageUploadZone
                  compact
                  labelInside
                  label={chartText('sizeChartPanel.imageLabel', '尺码表图片')}
                  useCase={TIKTOK_SIZE_CHART_IMAGE_USE_CASE}
                  value={sizeChartImage ? [sizeChartImage] : []}
                  shopId={shopId}
                  skipTiktokConvert={skipTiktokConvert}
                  enforceTiktokRules={false}
                  onChange={applySizeChartImage}
                  hint=""
                />
                <p className="tko-wizard__inline-hint">
                  {chartText('sizeChartPanel.imageHint', '上传尺码表图片即可（短边须 ≥1024px）。与「尺码表」表格二选一；切换回尺码表会清空已传图片。')}
                </p>
              </div>
            ) : (
              <p className="tko-wizard__inline-hint">{chartText('sizeChartPanel.pickShopImage', fixedUi("请先选择店铺后再上传尺码表图片。"))}</p>
            )
          ) : (
            <>
              {!hideLibraryActions ? (
                <div className="tko-size-chart-template-bar">
                  <select
                    className="tko-select"
                    value={selectedLibraryId}
                    disabled={busy}
                    onChange={(e) => applyLibraryTemplate(e.target.value)}
                    aria-label={officialMode ? chartText('sizeChartPanel.pickOfficialLib', '选择官方尺码库') : chartText('sizeChartPanel.pickChartLib', '选择尺码表库')}
                  >
                    <option value="">
                      {officialMode ? chartText('sizeChartPanel.fromOfficialOpt', '从官方尺码库选用（可选）') : chartText('sizeChartPanel.fromLibOpt', fixedUi("从尺码库选用（可选）"))}
                    </option>
                    {librarySelectOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  {!suitMulti && !officialMode ? (
                    <button
                      type="button"
                      className="tko-btn tko-btn--primary tko-btn--sm"
                      disabled={busy || !normalizedSizeLabels.length}
                      onClick={openSaveTemplateModal}
                    >
                      {chartText('sizeChartPanel.saveAsTemplate', '保存为模板')}
                    </button>
                  ) : null}
                  {suitMulti ? (
                    <span className="tko-wizard__inline-hint">
                      {chartText('sizeChartPanel.suitNeed', `套装类目请选择 ${needCount} 个尺码表（已选 ${selectedBusinessIds.length}/${needCount}）`, { need: needCount, picked: selectedBusinessIds.length })}
                    </span>
                  ) : null}
                </div>
              ) : null}

              {suitMulti && mode === 'template' && !hideLibraryActions ? (
                <ul className="tko-official-size-chart__tpl-list">
                  {selectedBusinessIds.map((id) => {
                    const sid = tiktokSizeChartId(id);
                    const t = platformTemplates.find((row) => (
                      tiktokSizeChartId(row.businessId) === sid
                      || tiktokSizeChartId(row.platformBusinessId) === sid
                      || tiktokSizeChartId(row.id) === sid
                    ));
                    return (
                      <li key={sid || String(id)}>
                        <button
                          type="button"
                          className="tko-official-size-chart__tpl is-on"
                          onClick={() => setSelectedBusinessIds((prev) => prev.filter(
                            (x) => tiktokSizeChartId(x) !== sid,
                          ))}
                          title={chartText('sizeChartPanel.unselect', '点击取消选择')}
                        >
                          {t?.name || chartText('sizeChartPanel.chartNo', `尺码表#${sid}`, { id: sid })} · {sid}
                        </button>
                      </li>
                    );
                  })}
                  {!selectedBusinessIds.length ? (
                    <li className="tko-wizard__inline-hint">{chartText('sizeChartPanel.pickFromLibAbove', fixedUi("请从上方尺码表库选择"))}</li>
                  ) : null}
                </ul>
              ) : null}

              {(!suitMulti || mode === 'create' || hideLibraryActions) ? (
                normalizedSizeLabels.length === 0 ? (
                  <p className="tko-wizard__inline-hint">{emptySizeHint === '请先在规格供应中选择尺码，尺寸表将按所选尺码自动增减行。'
            ? chartText('sizeChartPanel.emptySize', '请先在规格供应中选择尺码，尺寸表将按所选尺码自动增减行。')
            : emptySizeHint}</p>
                ) : (
                  <div className="tko-publish__table-wrap">
                    <table className="tko-publish__table tko-size-chart-table">
                      <thead>
                        <tr>
                          <th className="tko-size-chart-table__size">{groupList[0]?.name || chartText('sizeChartPanel.sizeCol', fixedUi("尺码"))}</th>
                          {elementList.map((el) => (
                            <th key={`${el.id}-${measureLabelVersion}`}>
                              {getCachedTiktokSizeChartMeasureLabel(el) || formatTiktokSizeChartMeasureLabel(el)}
                              {(el.necessary || !elementList.some((x) => x?.necessary)) ? <RequiredMark /> : null}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {records.map((row, ri) => (
                          <tr key={`${row.values[groupId] || ri}`}>
                            <td className="tko-size-chart-table__size">
                              {row.values[groupId] || '—'}
                            </td>
                            {elementList.map((el) => (
                              <td key={el.id}>
                                <input
                                  className="tko-input"
                                  value={row.values[String(el.id)] || ''}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    const id = String(el.id);
                                    setRecords((prev) => prev.map((r, i) => (
                                      i === ri ? { values: { ...r.values, [id]: v } } : r
                                    )));
                                    if (mode === 'template') setMode('create');
                                    setSelectedBusinessIds([]);
                                  }}
                                />
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              ) : null}
            </>
          )}
        </>
      ) : null}

      <SaveSizeChartTemplateMetaModal
        open={saveModalOpen}
        shopId={shopId}
        categoryId={categoryId}
        productTypeId={productTypeId}
        categoryPathLabel={categoryPathLabel}
        selectedSizeIds={selectedSizeIds}
        selectedSizeLabels={normalizedSizeLabels}
        sizeClass={sizeClass}
        officialSizeChart={officialDraftForSave}
        defaultStyleNo={defaultStyleNo}
        onClose={() => setSaveModalOpen(false)}
        onSaved={async () => {
          await reloadIpeTemplates();
          try {
            const templatesRes = await tiktokPlatformAPI.listPublishSizeChartTemplates(shopId, {
              catId: Number(categoryId),
              offset: 0,
              pageSize: 50,
            });
            setPlatformTemplates(asSizeChartList(templatesRes));
          } catch {
            /* ignore */
          }
        }}
      />
    </div>
  );
});

export default TiktokOfficialSizeChartPanel;

/** 拉取 class.get，供父组件判定必填 */
export async function fetchSizeChartClassRequired(shopId, categoryId) {
  try {
    const raw = await tiktokPlatformAPI.getPublishSizeChartClass(shopId, Number(categoryId));
    const parsed = parseSizeChartClass(raw);
    return { ok: true, ...parsed };
  } catch (e) {
    return { ok: false, required: false, classId: null, message: e?.message || chartText('sizeChartPanel.classRulesFail', '尺码表类目规则加载失败') };
  }
}
